import {
	Drop,
	Fire,
	HouseLine,
	Lightning,
	Thermometer,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type { TenantApartment } from "@/api/tenant/types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type ApartmentInfoCardProps = {
	apartment: TenantApartment;
	className?: string;
};

type MeterBucket = "water" | "heat" | "electricity" | "other";

function bucketFor(type: string): MeterBucket {
	const t = type.toLowerCase();
	if (t.includes("water")) return "water";
	if (t.includes("heat")) return "heat";
	if (t.includes("electric")) return "electricity";
	return "other";
}

export function ApartmentInfoCard({
	apartment,
	className,
}: ApartmentInfoCardProps) {
	const { t } = useTranslation();
	const meterCount = apartment.meters.length;

	const counts = apartment.meters.reduce<Record<MeterBucket, number>>(
		(acc, m) => {
			acc[bucketFor(m.type)] += 1;
			return acc;
		},
		{ water: 0, heat: 0, electricity: 0, other: 0 },
	);

	type Breakdown = {
		key: MeterBucket;
		icon: React.ReactNode;
		label: string;
		count: number;
		tone: string;
	};
	const breakdown: Breakdown[] = [
		{
			key: "water",
			icon: <Drop weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.water"),
			count: counts.water,
			tone: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
		},
		{
			key: "heat",
			icon: <Fire weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.heat"),
			count: counts.heat,
			tone: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
		},
		{
			key: "electricity",
			icon: <Lightning weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.electricity"),
			count: counts.electricity,
			tone: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
		},
		{
			key: "other",
			icon: <Thermometer weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.other"),
			count: counts.other,
			tone: "bg-muted text-muted-foreground",
		},
	];
	const visibleBreakdown = breakdown.filter((b) => b.count > 0);

	return (
		<Card
			className={cn(
				"relative overflow-hidden border-border/60 p-5 sm:p-6",
				className,
			)}
		>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute -right-12 -top-12 size-48 rounded-full bg-primary/[0.07] blur-2xl"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-transparent"
			/>

			<div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
				<div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary sm:size-16">
					<HouseLine weight="duotone" className="size-7 sm:size-8" />
				</div>

				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
						{t("tenant.apartment.addressLabel")}
					</p>
					<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
						<h1 className="truncate text-2xl font-semibold tracking-tight sm:text-3xl">
							{apartment.street}
						</h1>
						<span className="rounded-md bg-muted px-2 py-0.5 font-mono text-sm tabular-nums text-muted-foreground">
							{apartment.apartment_number}
						</span>
					</div>

					{visibleBreakdown.length > 0 ? (
						<div className="flex flex-wrap items-center gap-1.5">
							{visibleBreakdown.map((b) => (
								<span
									key={b.key}
									className={cn(
										"inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
										b.tone,
									)}
								>
									{b.icon}
									<span className="tabular-nums">{b.count}</span>
									<span>{b.label}</span>
								</span>
							))}
							<span className="text-xs text-muted-foreground">
								· {t("tenant.apartment.metersCount", { count: meterCount })}
							</span>
						</div>
					) : (
						<p className="text-sm text-muted-foreground">
							{t("tenant.apartment.noMeters")}
						</p>
					)}
				</div>
			</div>
		</Card>
	);
}
